import React, { useEffect, useState, useCallback } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowsLeftRight, CheckCircle, WarningCircle, XCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = [
  { id: "tally_vs_bank",  label: "Tally ↔ Bank",            color: "from-teal-500 to-cyan-600",
    activeCls: "data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white" },
  { id: "payout_vs_bank", label: "Swiggy / Zomato ↔ Bank",  color: "from-orange-500 to-amber-600",
    activeCls: "data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-600 data-[state=active]:text-white" },
  { id: "loan_director",  label: "Director / Loan",          color: "from-violet-500 to-purple-700",
    activeCls: "data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-700 data-[state=active]:text-white" },
];

export default function ReconciliationTab({ refreshKey }) {
  const [kind, setKind] = useState("tally_vs_bank");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/reconciliation", { params: { kind } });
      setItems(res.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const deletePayout = async (it) => {
    if (!window.confirm(`Delete this payout row?\n\n${it.label}\nExpected ${fmtINR(it.expected_amount)} on ${fmtDate(it.expected_date)}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/payouts/${it.id}`);
      toast.success("Payout removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const matched = items.filter((i) => i.status === "matched").length;
  const mismatches = items.filter((i) => i.status === "mismatch").length;
  const missing = items.filter((i) => i.status === "missing").length;
  const matchedAmt = items.filter((i) => i.status === "matched").reduce((s, i) => s + i.expected_amount, 0);
  const unmatchedAmt = items.filter((i) => i.status !== "matched").reduce((s, i) => s + i.expected_amount, 0);

  const activeKind = KINDS.find((k) => k.id === kind);

  return (
    <div data-testid="reconciliation-tab" className="space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm grid place-items-center bg-teal-100 text-teal-700"><ArrowsLeftRight size={15} weight="duotone" /></div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">Reconciliation worklist</h2>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile icon={<CheckCircle size={16} weight="fill" />} color="emerald" label="Paid" count={matched} amount={matchedAmt} />
        <SummaryTile icon={<WarningCircle size={16} weight="fill" />} color="amber" label="Pending" count={mismatches} amount={null} />
        <SummaryTile icon={<XCircle size={16} weight="fill" />} color="rose" label="Missing" count={missing} amount={null} />
        <SummaryTile icon={<ArrowsLeftRight size={16} weight="fill" />} color="indigo" label="Need attention (₹)" count={null} amount={unmatchedAmt} />
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="h-auto bg-white p-1 rounded-md border border-slate-200 shadow-sm">
          {KINDS.map((k) => (
            <TabsTrigger
              key={k.id}
              value={k.id}
              data-testid={`recon-tab-${k.id}`}
              className={`rounded-sm text-xs px-3 py-1.5 font-semibold text-slate-500 ${k.activeCls} data-[state=active]:shadow-sm`}
            >
              {k.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.id} value={k.id} className="mt-4">
            <div className="rounded-md border border-slate-200 bg-white overflow-hidden shadow-sm">
              <div className={`h-1 bg-gradient-to-r ${activeKind.color}`} />
              <div className="max-h-[calc(100vh-22rem)] overflow-auto" data-testid={`recon-scroll-${k.id}`}>
              <Table>
                <TableHeader
                  className="sticky top-0 z-20"
                  style={{ background: "linear-gradient(180deg,#102844 0%,#0c1f37 100%)" }}
                >
                  <TableRow className="hover:bg-transparent border-b border-slate-200">
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Party</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Expected date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold text-right">Expected amount</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold text-right">To be paid</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold text-right">Actual received in bank</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Note</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-slate-200 font-bold w-10"> </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={9} className="text-center text-sm text-slate-500 py-10">Loading…</TableCell></TableRow>}
                  {!loading && items.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-sm text-slate-500 py-10">No entries. Upload Tally &amp; Bank data.</TableCell></TableRow>
                  )}
                  {items.map((it) => (
                    <TableRow key={it.id} className="border-b border-slate-100 hover:bg-slate-50/60 group" data-testid={`recon-row-${it.id}`}>
                      <TableCell><StatusBadge status={it.status} /></TableCell>
                      <TableCell className="text-sm font-medium text-slate-900">{it.label}</TableCell>
                      <TableCell className="text-sm text-slate-600">{it.party || it.counterparty}</TableCell>
                      <TableCell className="text-sm text-slate-700">{fmtDate(it.expected_date)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-sm">{fmtINR(it.expected_amount)}</TableCell>
                      <TableCell className={`text-right font-mono tabular-nums text-sm font-bold ${
                        (it.to_be_paid ?? 0) > 0 ? "text-rose-300" : "text-emerald-300"
                      }`}>
                        {it.to_be_paid != null ? fmtINR(it.to_be_paid) : "—"}
                      </TableCell>
                      <TableCell className={`text-right font-mono tabular-nums text-sm ${it.actual_amount ? "text-emerald-300" : "text-slate-400"}`}>
                        {it.actual_amount ? fmtINR(it.actual_amount) : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{it.note || "—"}</TableCell>
                      <TableCell className="text-right">
                        {kind === "payout_vs_bank" ? (
                          <button
                            onClick={() => deletePayout(it)}
                            className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 p-1.5 rounded-sm transition-colors"
                            title="Delete this payout"
                            data-testid={`recon-delete-${it.id}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "matched") {
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50 rounded-sm uppercase tracking-wider text-[10px] font-bold gap-1"><CheckCircle size={11} weight="fill" /> Paid</Badge>;
  }
  if (status === "mismatch") {
    return <Badge className="bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50 rounded-sm uppercase tracking-wider text-[10px] font-bold gap-1"><WarningCircle size={11} weight="fill" /> Pending</Badge>;
  }
  return <Badge className="bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50 rounded-sm uppercase tracking-wider text-[10px] font-bold gap-1"><XCircle size={11} weight="fill" /> Missing</Badge>;
}

function SummaryTile({ icon, color, label, count, amount }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  }[color];
  return (
    <div className={`rounded-md border p-4 ${palette}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{label}</div>
        {icon}
      </div>
      <div className="font-mono tabular-nums text-xl font-bold mt-2">
        {count !== null ? count : fmtINR(amount)}
      </div>
    </div>
  );
}
